import { useApiWithHandling } from "../hooks/useApiWithHandling";
import { ApiResponse } from "./apiService";
import { createSHA256 } from "hash-wasm";

// Type definitions
export interface FileWithMetadata {
  file: File;
  id: string;
  uploadProgress: number;
  status: "pending" | "uploading" | "success" | "error";
  error?: string;
  documentId?: string;
}

export interface UploadProgress {
  [fileName: string]: number;
}

export interface ApiUploadResponse {
  documentId: string;
  title?: string;
  description?: string;
}

interface ApiEnvelope<T> {
  success: boolean;
  message?: string;
  data: T;
}

interface ApiDocumentDto {
  id: string;
  title?: string;
  description?: string;
}

interface DirectUploadSession {
  documentId: string;
  blobPath: string;
  uploadUrl: string;
  fileName: string;
  contentType: string;
  fileSizeInBytes: number;
}

export interface DocumentMetadata {
  tailNumber: string;
  selectedFolderCode: string;
  folderPath?: string;
  documentTypeCode: string;
  serialNumber: string;
  aircraftType: string;
  documentTitle: string;
  description: string;
  isQuickUpload: boolean;
  approvalStatus?: string;
  source?: string;
  assemblyType?: string;
  assemblyFolder?: string;
  assemblyCategory?: string;
  componentId?: string;
}

export interface CreateCustomFolderRequest {
  name: string;
  tailNumber: string;
  parentCustomFolderId?: string;
  attachedIataNodeKey?: string;
  componentId?: string;
  subAssemblyId?: string;
  assemblyType?: string;
}

export interface RenameCustomFolderRequest {
  folderId: string;
  name: string;
}

export interface DeleteCustomFolderRequest {
  folderId: string;
}

export interface DownloadRootFolderArchiveRequest {
  downloadScope: "airframe-root" | "warehouse-offwing-assembly-root" | "folder-path";
  tailNumber?: string;
  assemblyType?: string;
  assemblyFolder?: string;
  folderPath?: string;
  archiveRootName?: string;
}

export interface FolderExportJobResponse {
  jobId: string;
  exportLabel?: string;
  status: number;
  progressPercent: number;
  totalFiles: number;
  processedFiles: number;
  totalBytes: number;
  processedBytes: number;
  fileName?: string;
  downloadUrl?: string;
  downloadFiles?: Array<{
    fileName: string;
    downloadUrl: string;
    sizeBytes: number;
  }>;
  errorMessage?: string;
  createdAtUtc: string;
  startedAtUtc?: string;
  completedAtUtc?: string;
  expiresAtUtc?: string;
}

export interface DocumentMoveJobFileResponse {
  fileId: string;
  documentId: string;
  fileName: string;
  sourceBlobPath: string;
  targetBlobPath: string;
  status: string;
  failureReason?: string;
  wasRenamed?: boolean;
  originalFileName?: string;
  renamedToFileName?: string;
}

export interface DocumentMoveJobResponse {
  jobId: string;
  isFolderMove: boolean;
  sourceFolderName?: string;
  sourceFolderPath?: string;
  targetFolderName: string;
  targetFolderPath: string;
  tailNumber: string;
  assemblyType?: string;
  assemblyFolder?: string;
  componentId?: string;
  hasFileMoves?: boolean;
  hasFolderMoves?: boolean;
  initiatedBy: string;
  status: string;
  totalFileCount: number;
  completedFileCount: number;
  failedFileCount: number;
  conflictResolution?: string;
  createdAt: string;
  completedAt?: string;
  files?: DocumentMoveJobFileResponse[];
}

const GUID_PATTERN =
  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[1-5][0-9a-fA-F]{3}-[89abAB][0-9a-fA-F]{3}-[0-9a-fA-F]{12}$/;

/**
 * Some custom-folder paths can come back with the tenant GUID prefixed twice.
 * Collapse only that specific duplicate prefix to keep downstream archive paths stable.
 */
export const normalizeSelectedFolderCode = (folderCode: string): string => {
  if (!folderCode) {
    return folderCode;
  }

  const segments = folderCode
    .split(/[\\/]+/)
    .map((segment) => segment.trim())
    .filter(Boolean);

  if (segments.length >= 2 && segments[0] === segments[1] && GUID_PATTERN.test(segments[0])) {
    segments.splice(0, 1);
  }

  return segments.join("/");
};

// Constants
export const MAX_FILE_SIZE_GB = 5;
export const MAX_FILES = 100;
export const MAX_TOTAL_SIZE_GB = 1024;
export const DIRECT_BLOB_UPLOAD_THRESHOLD_BYTES = 99 * 1024 * 1024;
export const ALLOWED_TYPES: Record<string, string[]> = {
  "application/pdf": ["pdf"],
  "application/msword": ["doc"],
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document": ["docx"],
  "application/vnd.ms-excel": ["xls"],
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": ["xlsx"],
  // Office Open XML packages and real ZIP archives may both report application/zip.
  "application/zip": ["docx", "xlsx", "zip"],
  "application/x-zip-compressed": ["zip"],
  "text/plain": ["txt"],
  "image/heic": ["heic"],
  "image/heif": ["heif"],
  "image/jpeg": ["jpg", "jpeg"],
  "image/png": ["png"],
  "image/gif": ["gif"],
  "image/tiff": ["tiff", "tif"],
  "text/xml": ["xml", "xmls"],
  "application/xml": ["xml", "xmls"],
  "video/x-ms-wmv": ["wmv"],
  "video/mp4": ["mp4"],
  "video/x-msvideo": ["avi"],
  "video/avi": ["avi"],
  "video/msvideo": ["avi"],
  "video/mpeg": ["mpeg"],
  "text/html": ["html"],
};


export const ALLOWED_EXTENSIONS = [
  ".pdf",
  ".doc",
  ".docx",
  ".xls",
  ".xlsx",
  ".txt",
  ".heic",
  ".heif",
  ".jpg",
  ".jpeg",
  ".png",
  ".tif",
  ".tiff",
  ".xml", 
  ".xmls",
  ".wmv", 
  ".mp4",
  ".avi",
  ".mpeg",
  ".zip",
  ".html",
];

// Custom hook for document upload operations
export const useDocumentService = () => {
  const { get, post, put, del } = useApiWithHandling();

const shouldUseDirectBlobUpload = (file: File): boolean => {
  // HEIC/HEIF and AVI are stored as originals; HEIC is converted on preview download/view.
  return file.size > DIRECT_BLOB_UPLOAD_THRESHOLD_BYTES;
};

const buildUploadPayload = (metadata: DocumentMetadata) => ({
  tailNumber: metadata.tailNumber,
  selectedFolderCode: normalizeSelectedFolderCode(metadata.selectedFolderCode),
  folderPath: metadata.folderPath ?? "",
  documentTypeCode: metadata.documentTypeCode,
  aircraftType: metadata.aircraftType,
  serialNumber: metadata.serialNumber,
  documentTitle: metadata.documentTitle,
  description: metadata.description,
  isQuickUpload: metadata.isQuickUpload,
  approvalStatus: metadata.approvalStatus ?? "Pending",
  source: metadata.source ?? "DMSApp",
  assemblyType: metadata.assemblyType,
  assemblyFolder: metadata.assemblyFolder,
  assemblyCategory: metadata.assemblyCategory,
  componentId: metadata.componentId,
});

const extractPayload = <T,>(response: ApiResponse<ApiEnvelope<T>> | null): T | null => {
  return response?.data?.data ?? null;
};

const normalizeUploadResponse = (payload: ApiDocumentDto | null): ApiUploadResponse | null => {
  if (!payload) {
    return null;
  }

  return {
    documentId: payload.id,
    title: payload.title,
    description: payload.description,
  };
};

const buildMultipartFormData = (file: File, metadata: DocumentMetadata) => {
  const normalizedSelectedFolderCode = normalizeSelectedFolderCode(
    metadata.selectedFolderCode
  );

  const formData = new FormData();
  formData.append("file", file);
  formData.append("tailNumber", metadata.tailNumber);
  formData.append("selectedFolderCode", normalizedSelectedFolderCode);
  if (metadata.folderPath) {
    formData.append("folderPath", metadata.folderPath);
  }
  formData.append("documentTypeCode", metadata.documentTypeCode);
  formData.append("aircraftType", metadata.aircraftType);
  formData.append("serialNumber", metadata.serialNumber);
  formData.append("documentTitle", metadata.documentTitle);
  formData.append("description", metadata.description);
  formData.append("isQuickUpload", metadata.isQuickUpload.toString());
  formData.append("approvalStatus", metadata.approvalStatus ?? "Pending");
  formData.append("source", metadata.source ?? "DMSApp");
  if (metadata.assemblyType) {
    formData.append("assemblyType", metadata.assemblyType);
  }
  if (metadata.assemblyFolder) {
    formData.append("assemblyFolder", metadata.assemblyFolder);
  }
  if (metadata.assemblyCategory) {
    formData.append("assemblyCategory", metadata.assemblyCategory);
  }
  if (metadata.componentId) {
    formData.append("componentId", metadata.componentId);
  }

  return formData;
};

const createHashForFile = async (file: File) => {
  const sha256 = await createSHA256();

  if (typeof file.stream !== "function") {
    const buffer = await new Promise<ArrayBuffer>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as ArrayBuffer);
      reader.onerror = () => reject(reader.error ?? new Error("Failed to read file for hashing."));
      reader.readAsArrayBuffer(file);
    });

    sha256.update(new Uint8Array(buffer));
    return sha256.digest("hex");
  }

  const reader = file.stream().getReader();

  while (true) {
    const { done, value } = await reader.read();
    if (done) {
      break;
    }

    sha256.update(value);
  }

  return sha256.digest("hex");
};

const uploadDirectToBlob = async (session: DirectUploadSession, file: File) => {
  const response = await fetch(session.uploadUrl, {
    method: "PUT",
    headers: {
      "x-ms-blob-type": "BlockBlob",
      "Content-Type": session.contentType || file.type || "application/octet-stream",
    },
    body: file,
  });

  if (response.ok) {
    return;
  }

  throw new Error(`Direct blob upload failed with status ${response.status}.`);
};

const uploadSmallDocument = async (
  id: string,
  file: File,
  metadata: DocumentMetadata
) => {
  const endpoint = `api/documents/${id}/upload`;
  const formData = buildMultipartFormData(file, metadata);

  const response = await post<ApiEnvelope<ApiDocumentDto>>(
    endpoint,
    formData,
    {
      showLoader: true,
      loadingMessage: `Uploading ${file.name}...`,
      showErrorSnackbar: false,
      throwOnError: true,
    },
    true // requireAuth
  );

  return normalizeUploadResponse(extractPayload(response));
};

const uploadLargeDocument = async (
  id: string,
  file: File,
  metadata: DocumentMetadata
) => {
  const payload = buildUploadPayload(metadata);
  const prepareResponse = await post<ApiEnvelope<DirectUploadSession>>(
    `api/documents/${id}/direct-upload/prepare`,
    {
      ...payload,
      fileName: file.name,
      contentType: file.type || "application/octet-stream",
      fileSizeInBytes: file.size,
    },
    {
      showLoader: true,
      loadingMessage: `Preparing upload for ${file.name}...`,
      showErrorSnackbar: false,
      throwOnError: true,
    },
    true
  );

  const session = extractPayload(prepareResponse);
  if (!session) {
    throw new Error("Failed to prepare large file upload.");
  }

  const hash = await createHashForFile(file);
  await uploadDirectToBlob(session, file);

  const finalizeResponse = await post<ApiEnvelope<ApiDocumentDto>>(
    `api/documents/${id}/direct-upload/finalize`,
    {
      ...payload,
      fileName: session.fileName,
      contentType: session.contentType,
      fileSizeInBytes: session.fileSizeInBytes,
      hash,
    },
    {
      showLoader: true,
      loadingMessage: `Finalizing ${file.name}...`,
      showErrorSnackbar: false,
      throwOnError: true,
    },
    true
  );

  return normalizeUploadResponse(extractPayload(finalizeResponse));
};

const uploadDocument = async (
  file: File,
  metadata: DocumentMetadata
) => {
  const id = crypto.randomUUID();

  if (shouldUseDirectBlobUpload(file)) {
    return uploadLargeDocument(id, file, metadata);
  }

  return uploadSmallDocument(id, file, metadata);
};

const updateDocument = async (payload: {
  documentId: string;
  tailNumber: string;
  categoryCode: string;    
  serialNumber: string;
  aircraftType: string;
  documentTitle: string;
  description: string;
}) => {
  const { documentId, ...metadata } = payload;

  const endpoint = `api/documents/${documentId}/metadata`;

  const response = await put(
    endpoint,
    metadata,
    {
      showLoader: true,
      loadingMessage: "Updating document metadata...",
      showErrorSnackbar: true,
      showSuccessSnackbar: false,
    },
    true
  );

  return response?.data || null;
};

const deleteDocument = async (documentId: string) => {
  const response = await del(
    `api/documents/${documentId}`,
    {
      showLoader: true,
      loadingMessage: "Deleting document...",
      showErrorSnackbar: true,
      showSuccessSnackbar: false,
    },
    true
  );

  return response !== null;
};

const createCustomFolder = async (request: CreateCustomFolderRequest) => {
  const payload = {
    name: request.name,
    tailNumber: request.tailNumber,
    parentCustomFolderId: request.parentCustomFolderId ?? null,
    attachedIataNodeKey: request.attachedIataNodeKey ?? null,
    componentId: request.componentId ?? null,
    subAssemblyId: request.subAssemblyId ?? null,
    assemblyType: request.assemblyType ?? null,
  };

  const response = await post<ApiResponse<string>>(
    "api/documents/custom-folder",
    payload,
    {
      showLoader: true,
      loadingMessage: "Creating folder...",
      showErrorSnackbar: true,
      showSuccessSnackbar: false,
    },
    true
  );

  return response?.data?.data ?? null;
};

const renameCustomFolder = async (request: RenameCustomFolderRequest) => {
  const response = await put<ApiResponse<boolean>>(
    `api/documents/custom-folder/${request.folderId}/rename`,
    {
      folderId: request.folderId,
      name: request.name,
    },
    {
      showLoader: true,
      loadingMessage: "Renaming folder...",
      showErrorSnackbar: true,
      showSuccessSnackbar: false,
    },
    true
  );

  return response?.data?.data ?? null;
};

const deleteCustomFolder = async (request: DeleteCustomFolderRequest) => {
  const response = await del(
    `api/documents/custom-folder/${request.folderId}`,
    {
      showLoader: true,
      loadingMessage: "Deleting folder...",
      showErrorSnackbar: true,
      showSuccessSnackbar: false,
    },
    true
  );

  return response !== null;
};

const startFolderExportJob = async (request: DownloadRootFolderArchiveRequest) => {
  const response = await post<ApiResponse<FolderExportJobResponse>>(
    "api/documents/exports",
    {
      downloadScope: request.downloadScope,
      tailNumber: request.tailNumber,
      assemblyType: request.assemblyType,
      assemblyFolder: request.assemblyFolder,
      folderPath: request.folderPath,
      archiveRootName: request.archiveRootName,
    },
    {
      showLoader: false,
      showErrorSnackbar: true,
      showSuccessSnackbar: false,
    },
    true
  );

  return response?.data?.data ?? null;
};

const getFolderExportJob = async (jobId: string) => {
  const response = await get<ApiResponse<FolderExportJobResponse>>(
    `api/documents/exports/${jobId}`,
    {
      showLoader: false,
      showErrorSnackbar: false,
      showSuccessSnackbar: false,
    },
    true
  );

  return response?.data?.data ?? null;
};

const listFolderExportJobs = async (take = 50) => {
  const response = await get<ApiResponse<FolderExportJobResponse[]>>(
    `api/documents/exports?take=${take}`,
    {
      showLoader: false,
      showErrorSnackbar: true,
      showSuccessSnackbar: false,
    },
    true
  );

  return response?.data?.data ?? [];
};

const listMoveJobs = async (take = 50) => {
  const response = await get<ApiResponse<DocumentMoveJobResponse[]>>(
    `api/documents/move-jobs?take=${take}`,
    {
      showLoader: false,
      showErrorSnackbar: true,
      showSuccessSnackbar: false,
    },
    true
  );

  return response?.data?.data ?? [];
};

const getMoveJob = async (jobId: string) => {
  const response = await get<ApiResponse<DocumentMoveJobResponse>>(
    `api/documents/move-jobs/${jobId}`,
    {
      showLoader: false,
      showErrorSnackbar: false,
      showSuccessSnackbar: false,
    },
    true
  );

  return response?.data?.data ?? null;
};

const logFolderExportDownload = async (jobId: string, fileCount: number) => {
  await post<ApiResponse<object>>(
    `api/documents/exports/${jobId}/download-audit`,
    {
      fileCount,
    },
    {
      showLoader: false,
      showErrorSnackbar: false,
      showSuccessSnackbar: false,
    },
    true
  );
};

const retryFolderExportJob = async (jobId: string) => {
  const response = await post<ApiResponse<FolderExportJobResponse>>(
    `api/documents/exports/${jobId}/retry`,
    {},
    {
      showLoader: false,
      showErrorSnackbar: true,
      showSuccessSnackbar: false,
    },
    true
  );

  return response?.data?.data ?? null;
};

const retryMoveJob = async (jobId: string) => {
  const response = await post<ApiResponse<DocumentMoveJobResponse>>(
    `api/documents/move-jobs/${jobId}/retry`,
    {},
    {
      showLoader: false,
      showErrorSnackbar: true,
      showSuccessSnackbar: false,
    },
    true
  );

  return response?.data?.data ?? null;
};

  return {
    uploadDocument,
    updateDocument,
    deleteDocument,
    createCustomFolder,
    renameCustomFolder,
    deleteCustomFolder,
    startFolderExportJob,
    getFolderExportJob,
    listFolderExportJobs,
    logFolderExportDownload,
    retryFolderExportJob,
    listMoveJobs,
    getMoveJob,
    retryMoveJob,
  };
};
